import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useMappingDepartments } from './hooks/useMappingDepartments';
import { useModal } from './hooks/useModal';
import { DepartmentTable } from './components/DepartmentTable';
import { DepartmentForm } from './components/DepartmentForm';

export default function MappingDepartmentsPage() {
  const {
    departments,
    loading,
    error,
    addDepartment,
    editDepartment,
    deleteDepartment,
    editDepartment: updateRequisition,
  } = useMappingDepartments();

  const formModal = useModal();
  const deleteModal = useModal();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleAddOpen = () => formModal.open(null);

  const handleEditOpen = dept => formModal.open(dept);

  const handleDeleteOpen = dept => deleteModal.open(dept);

  const handleFormSubmit = async formData => {
    setSaving(true);
    try {
      if (formModal.data) {
        await editDepartment(formData);
      } else {
        await addDepartment(formData);
      }
      formModal.close();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await deleteDepartment(deleteModal.data.deptNumberDoNotEdit);
      deleteModal.close();
    } finally {
      setDeleting(false);
    }
  };

  const handleRequisitionChange = async (deptNumber, newValue) => {
    const dept = departments.find(d => d.deptNumberDoNotEdit === deptNumber);
    if (!dept) return;
    await updateRequisition({ ...dept, requisitionType: newValue });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* PAGE HEADER */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '18px' }}>
            Mapping Departments
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            {departments.length} department{departments.length !== 1 ? 's' : ''} configured
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddOpen}
          sx={{
            bgcolor: '#0C9344',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '13px',
            px: 2.5,
            py: 1,
            '&:hover': { bgcolor: '#0C9344' },
          }}>
          Add Department
        </Button>
      </Box>

      {/* ERROR */}
      {error && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fef2f2', border: '1px solid #fecdd3', borderRadius: '8px' }}>
          <Typography sx={{ color: '#b91c1c', fontSize: 13 }}>{error}</Typography>
        </Box>
      )}

      {/* TABLE */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} sx={{ color: '#0C9344' }} />
        </Box>
      ) : (
        <DepartmentTable
          departments={departments}
          onEdit={handleEditOpen}
          onDelete={handleDeleteOpen}
          onRequisitionChange={handleRequisitionChange}
        />
      )}

      {/* ADD / EDIT DIALOG */}
      <Dialog
        open={formModal.isOpen}
        onClose={formModal.close}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 0.5 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#0f172a' }}>
            {formModal.data ? 'Edit Department' : 'Add Department'}
          </Typography>
          <IconButton size="small" onClick={formModal.close} sx={{ color: '#94a3b8' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <DepartmentForm
            initial={formModal.data}
            onSubmit={handleFormSubmit}
            onCancel={formModal.close}
            loading={saving}
            departments={departments}
          />
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRM DIALOG */}
      <Dialog
        open={deleteModal.isOpen}
        onClose={deleteModal.close}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '16px', color: '#0f172a' }}>Delete Department</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '14px', color: '#475569' }}>
            Are you sure you want to delete{' '}
            <strong>
              {deleteModal.data?.deptNumberDeptNameForReporting || `Dept #${deleteModal.data?.deptNumberDoNotEdit}`}
            </strong>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={deleteModal.close}
            disabled={deleting}
            sx={{ borderRadius: '8px', textTransform: 'none', borderColor: '#e2e8f0', color: '#475569' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: '#ef4444', '&:hover': { bgcolor: '#b91c1c' } }}>
            {deleting ? 'Deleting…' : 'Yes, delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
